import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { CONFIG } from '@utils/constants';
import type { ApiError } from '@types';

// Extend axios config to support custom _retry property
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// ============================================
// Axios Instance Configuration
// ============================================

/**
 * Centrale Axios instance voor alle API calls.
 * 
 * Regels (zie 01_FRONTEND_GUARDRAILS.md):
 * - ALLE API calls MOETEN via deze instance
 * - withCredentials: true voor httpOnly cookies
 * - Error handling is gecentraliseerd
 * - Nooit directe axios/fetch calls in componenten!
 */

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: CONFIG.API_URL,
  withCredentials: true, // Voor httpOnly cookies
  timeout: 10000, // 10 seconden timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// Request Interceptors
// ============================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log requests in development (niet in production)
    if (CONFIG.LOG_LEVEL === 'debug' && !config.url?.includes('/health')) {
      console.log(`[API REQUEST] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error: AxiosError) => {
    // Request error (bijv. netwerk probleem)
    console.error('[API REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// ============================================
// Response Interceptors
// ============================================

api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (CONFIG.LOG_LEVEL === 'debug' && !response.config.url?.includes('/health')) {
      console.log(`[API RESPONSE] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    // Log error in development
    if (CONFIG.LOG_LEVEL === 'debug') {
      console.error('[API RESPONSE ERROR]', error);
    }
    
    // Handle different error types
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;
    
    switch (status) {
      case 400: // Bad Request
        console.error('Bad Request:', message);
        toast.error(message || 'Ongeldig verzoek');
        break;
        
      case 401: // Unauthorized - backend refresht token automatisch via cookies
      case 403: // Forbidden - backend refresht token automatisch via cookies
        console.error('Unauthorized/Forbidden:', message);
        if (!window.location.pathname.startsWith('/login') && error.config) {
          // Voorkom oneindige loops: check of dit al een retry is
          if (!error.config._retry) {
            error.config._retry = true;
            try {
              // Probeer 1x te retrien (backend ververst token automatisch via cookies)
              const retryResponse = await api(error.config as AxiosRequestConfig);
              return retryResponse;
            } catch (retryError) {
              // Retry faalde -> redirect naar login
              window.location.href = '/login';
            }
          } else {
            // Al een retry gedaan -> redirect naar login
            window.location.href = '/login';
          }
        }
        break;
        
      case 404: // Not Found
        console.error('Not Found:', message);
        toast.error('Resource niet gevonden');
        break;
        
      case 409: // Conflict (bv. dubbele naam in sessie)
        console.error('Conflict:', message);
        // Laat de caller de errors afhandelen
        break;
        
      case 422: // Unprocessable Entity (validation error)
        console.error('Validation Error:', message);
        // Laat de caller de errors afhandelen
        break;
        
      case 429: // Too Many Requests
        console.error('Rate Limited:', message);
        toast.error('Te veel verzoeken. Wacht een moment en probeer opnieuw.');
        break;
        
      case 500: // Internal Server Error
      case 502: // Bad Gateway
      case 503: // Service Unavailable
        console.error('Server Error:', message);
        toast.error('Er is een serverfout opgetreden. Probeer het later opnieuw.');
        break;
        
      default:
        // Network errors (geen response)
        if (!error.response) {
          console.error('Network Error:', error.message);
          toast.error('Geen verbinding met de server. Controleer je internetverbinding.');
        }
    }
    
    // Always reject the promise so callers can handle it
    return Promise.reject(error);
  }
);



// ============================================
// Export
// ============================================

export { api };
export default api;
